<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Eye,
  EyeClosed,
  Trash2,
  MapPin,
  FileText,
  Map,
  Edit2,
  ChevronRight,
  Ellipsis
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import FormFeature from '@/components/forms/FormFeature.vue'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty
} from '@/components/ui/table'

const props = defineProps({
  fileGroups: {
    type: Array,
    required: true
  },
  isUploading: {
    type: Boolean,
    default: false
  },
  selectedProject: {
    type: Object,
    default: null
  }
})

const store = useStore()
const allTemplates = computed(() => store.getters['geo/getAllTemplates'] || [])

const emit = defineEmits([
  'preview-feature',
  'edit-feature',
  'delete-feature',
  'bulk-edit',
  'upload-selected',
  'validate-feature',
  'update-feature',
  'feature-uploaded',
  'toggle-file-visibility',
  'toggle-file-group-polygons',
  'zoom-to-file'
])

const expandedRows = ref(new Set())
const expandedFiles = ref(new Set())

// Derived from fileGroups
const allFeatures = computed(() => {
  return props.fileGroups.flatMap(g => g.features)
})

const validFeatures = computed(() => {
  return allFeatures.value.filter(feature =>
    getValidationStatus(feature).status === 'valid'
  )
})

const hasMultiPolygon = (group) => {
  return group.features.some(f =>
    f.geometry?.type === 'MultiPolygon' ||
    f.properties?._parentUuid // split features indicate MultiPolygon source
  )
}

// Methods
const getValidationStatus = (feature) => {
  const issues = []
  const warnings = []

  if (!feature) {
    issues.push('Invalid feature structure')
    return { status: 'error', icon: XCircle, class: 'text-red-500', priority: 2, issues }
  }

  if (!feature.geometry || !feature.geometry.type) {
    issues.push('Missing geometry')
  } else if (feature.properties?.needsLocation) {
    warnings.push('No GPS data — location set to map center')
  } else {
    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length < 2 ||
          coords[0] === null || coords[1] === null ||
          Math.abs(coords[0]) > 180 || Math.abs(coords[1]) > 90) {
        issues.push('Invalid coordinates')
      }
    } else if (feature.geometry.type === 'LineString') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length < 2) {
        issues.push('LineString requires at least 2 points')
      }
    } else if (feature.geometry.type === 'Polygon') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length === 0 || coords[0].length < 4) {
        issues.push('Polygon requires at least 4 points')
      }
    } else if (feature.geometry.type === 'MultiPolygon') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length === 0) {
        issues.push('MultiPolygon requires at least 1 polygon')
      } else {
        for (let i = 0; i < coords.length; i++) {
          if (!coords[i] || coords[i].length === 0 || coords[i][0].length < 4) {
            issues.push(`MultiPolygon polygon ${i + 1} requires at least 4 points`)
            break
          }
        }
      }
    }
  }

  const hasProperties = feature.properties && typeof feature.properties === 'object'

  if (hasProperties && !feature.properties.name && !feature.properties.fileName) {
    warnings.push('Missing name')
  }

  const hasDescription = hasProperties && feature.properties.description &&
    typeof feature.properties.description === 'string' &&
    feature.properties.description.trim() !== ''

  if (issues.length === 0) {
    let statusText = 'Valid'
    if (hasDescription) {
      statusText = 'Valid (with description)'
    } else if (hasProperties) {
      statusText = 'Valid (with properties)'
    } else {
      statusText = 'Valid (geometry only)'
    }

    return {
      status: 'valid',
      icon: CheckCircle,
      class: 'text-green-500',
      priority: 0,
      statusText,
      warnings
    }
  } else if (issues.some(issue => issue.includes('geometry') || issue.includes('coordinates') || issue.includes('points'))) {
    return { status: 'error', icon: XCircle, class: 'text-red-500', priority: 2, issues, warnings }
  } else {
    return { status: 'warning', icon: AlertTriangle, class: 'text-yellow-500', priority: 1, issues, warnings }
  }
}

const getGeometryIcon = (type) => {
  switch (type) {
    case 'Point': return MapPin
    case 'LineString': return Edit2
    case 'Polygon': return Map
    default: return FileText
  }
}

const toggleFileExpansion = (fileName) => {
  if (expandedFiles.value.has(fileName)) {
    expandedFiles.value.delete(fileName)
  } else {
    expandedFiles.value.add(fileName)
  }
}

const isFileExpanded = (fileName) => {
  return expandedFiles.value.has(fileName)
}

// Row expansion methods
const toggleRowExpansion = (feature, index) => {
  const key = getFeatureKey(feature, index)
  if (expandedRows.value.has(key)) {
    expandedRows.value.delete(key)
  } else {
    expandedRows.value.add(key)
  }
}

const isRowExpanded = (feature, index) => {
  const key = getFeatureKey(feature, index)
  return expandedRows.value.has(key)
}

const formatGeometryCoordinates = (geometry) => {
  if (!geometry || !geometry.coordinates) return 'N/A'

  switch (geometry.type) {
    case 'Point':
      return `[${geometry.coordinates[0].toFixed(6)}, ${geometry.coordinates[1].toFixed(6)}]`
    case 'LineString':
      return `${geometry.coordinates.length} points`
    case 'Polygon':
      return `${geometry.coordinates[0].length} points (outer ring)`
    case 'MultiPoint':
      return `${geometry.coordinates.length} points`
    case 'MultiLineString':
      return `${geometry.coordinates.length} lines`
    case 'MultiPolygon':
      return `${geometry.coordinates.length} polygons`
    case 'GeometryCollection':
      return `${geometry.geometries.length} geometries`
    default:
      return 'Complex geometry'
  }
}

// Helper function to get a unique key for a feature
const getFeatureKey = (feature, index) => {
  if (feature.properties?.local_uuid) {
    return feature.properties.local_uuid
  }

  if (feature.geometry) {
    const geomType = feature.geometry.type
    const coords = JSON.stringify(feature.geometry.coordinates)
    return `${geomType}-${index}-${coords.substring(0, 50)}`
  }

  return `feature-${index}`
}

// Auto-expand rows for features that need location adjustment
watch(() => props.fileGroups, () => {
  for (const group of props.fileGroups) {
    group.features.forEach((feature, index) => {
      if (feature.properties?.needsLocation) {
        const key = getFeatureKey(feature, index)
        if (!expandedRows.value.has(key)) {
          expandedRows.value.add(key)
        }
        // Also expand the file group
        if (!expandedFiles.value.has(group.fileName)) {
          expandedFiles.value.add(group.fileName)
        }
      }
    })
  }
}, { immediate: true, deep: true })

// Clear expanded rows when file group polygons toggle changes
watch(() => props.fileGroups.map(g => g.groupPolygons), () => {
  // Split features get new UUIDs, so clear expanded rows for affected files
  expandedRows.value.clear()
}, { deep: true })

// Prepare feature for FormFeature component
const prepareFeatureForForm = (feature) => {
  const prepared = {
    type: 'Feature',
    geometry: feature.geometry,
    properties: {
      ...feature.properties,
      name: feature.properties?.name || feature.properties?.fileName?.split('.').shift() || 'Unnamed Feature',
      description: feature.properties?.description || '',
      color: feature.properties?.color || '#0059ff',
      shared: feature.properties?.shared || false,
      is_static: feature.properties?.is_static || false,
      created_origin: feature.properties?.created_origin || Date.now(),
      project_id: props.selectedProject?.project_id || feature.properties?.project_id,
      isUpload: false
    }
  }
  return prepared
}

// Handle feature creation from form
const handleFeatureCreated = (originalFeature, createdFeature) => {
  emit('feature-uploaded', originalFeature, createdFeature)
  const key = getFeatureKey(originalFeature, 0)
  expandedRows.value.delete(key)
}

const handleFeatureSaved = (originalFeature, savedFeature) => {
  // Handle feature save (for edit mode, if needed)
}

const handleFeatureUpdate = (originalFeature, updatedFeature) => {
  Object.assign(originalFeature, updatedFeature)
  emit('update-feature', originalFeature)
}

const deleteAllFileFeatures = (group) => {
  group.features.forEach(feature => {
    emit('delete-feature', feature)
  })
}
</script>

<template>
  <div class="space-y-4">
    <!-- Header with stats -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <div class="text-sm text-muted-foreground">
          {{ fileGroups.length }} {{ fileGroups.length === 1 ? 'file' : 'files' }},
          {{ allFeatures.length }} {{ allFeatures.length === 1 ? 'geometry' : 'geometries' }}
        </div>
      </div>

      <div class="flex items-center space-x-2">
        <Button
          v-if="validFeatures.length > 0"
          @click="emit('upload-selected', validFeatures)"
          size="sm"
          :disabled="isUploading || !selectedProject"
          :title="!selectedProject ? 'Select a project first' : ''"
        >
          Upload All ({{ validFeatures.length }})
        </Button>
        <div v-if="!selectedProject" class="text-sm text-yellow-600">
          Select a project to enable upload
        </div>
      </div>
    </div>

    <!-- Summary badges - only show errors if present -->
    <div v-if="allFeatures.filter(f => getValidationStatus(f).status === 'error').length > 0" class="flex items-center space-x-2">
      <Badge variant="secondary" class="text-red-600">
        <XCircle class="w-3 h-3 mr-1" />
        {{ allFeatures.filter(f => getValidationStatus(f).status === 'error').length }} Errors
      </Badge>
    </div>

    <!-- File Groups -->
    <div class="space-y-2">
      <div
        v-for="group in fileGroups"
        :key="group.fileName"
        class="border rounded-lg overflow-hidden"
      >
        <!-- File Header Row -->
        <div
          class="flex items-center px-3 py-2 bg-muted/30 hover:bg-muted/50 cursor-pointer"
          @click="toggleFileExpansion(group.fileName)"
        >
          <ChevronRight
            class="w-4 h-4 mr-2 shrink-0 transition-transform duration-200"
            :class="{ 'rotate-90': isFileExpanded(group.fileName) }"
          />

          <div class="flex-1 min-w-0 flex items-center space-x-2">
            <span class="font-medium text-sm truncate">{{ group.fileName }}</span>
            <Badge variant="secondary" class="text-xs shrink-0">
              {{ group.features.length }}
            </Badge>
          </div>

          <div class="flex items-center space-x-1 shrink-0" @click.stop>
            <!-- Visibility toggle (stays inline) -->
            <Button
              size="sm"
              variant="ghost"
              class="w-7 h-7 p-0"
              @click="emit('toggle-file-visibility', group.fileName)"
              :title="group.visible ? 'Hide on map' : 'Show on map'"
            >
              <Eye v-if="group.visible" class="w-4 h-4 text-primary" />
              <EyeClosed v-else class="w-4 h-4 text-muted-foreground" />
            </Button>

            <!-- 3-dots dropdown menu -->
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" class="w-7 h-7 p-0" type="button">
                  <Ellipsis class="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="w-48">
                <DropdownMenuGroup>
                  <!-- Group MultiPolygons toggle (conditional) -->
                  <DropdownMenuItem
                    v-if="hasMultiPolygon(group)"
                    class="cursor-pointer"
                    @select.prevent
                    @click="emit('toggle-file-group-polygons', group.fileName)"
                  >
                    <Switch :model-value="group.groupPolygons" class="scale-75 mr-2" />
                    <span>Group Polygons</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator v-if="hasMultiPolygon(group)" />
                  <!-- View on Map -->
                  <DropdownMenuItem
                    class="cursor-pointer"
                    @click="emit('zoom-to-file', group.fileName)"
                  >
                    <Map class="w-4 h-4 mr-2" />
                    <span>View on Map</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <!-- Delete -->
                  <DropdownMenuItem
                    class="cursor-pointer text-red-600 focus:text-red-600"
                    @click="deleteAllFileFeatures(group)"
                  >
                    <Trash2 class="w-4 h-4 mr-2" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <!-- Collapsible Geometry Rows -->
        <div v-if="isFileExpanded(group.fileName)">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead class="pl-8 py-1">Name</TableHead>
                <TableHead class="py-1">Type</TableHead>
                <TableHead class="py-1"></TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              <template v-for="(feature, index) in group.features" :key="getFeatureKey(feature, index)">
                <TableRow class="hover:bg-muted/50">
                  <!-- Name -->
                  <TableCell class="min-w-0 pl-8">
                    <div class="font-medium truncate max-w-40">
                      {{
                        feature.properties?.name ||
                        feature.properties?.fileName?.split('.').shift() ||
                        feature.geometry?.type ||
                        'Feature'
                      }}
                    </div>
                  </TableCell>

                  <!-- Type -->
                  <TableCell>
                    <div class="flex items-center space-x-2">
                      <component
                        v-if="getValidationStatus(feature).status === 'error'"
                        :is="getValidationStatus(feature).icon"
                        :class="getValidationStatus(feature).class"
                        class="w-3.5 h-3.5"
                        :title="getValidationStatus(feature).issues?.join(', ')"
                      />
                      <component
                        v-else-if="feature.properties?.needsLocation"
                        :is="AlertTriangle"
                        class="w-3.5 h-3.5 text-yellow-500"
                        title="No GPS data — location set to map center"
                      />
                      <span class="text-sm">{{ feature.properties?.needsLocation ? 'Image' : (feature.geometry?.type || 'Unknown') }}</span>
                    </div>
                  </TableCell>

                  <!-- Actions -->
                  <TableCell>
                    <div class="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        @click="emit('preview-feature', feature)"
                        class="h-8 w-8 p-0"
                        title="Preview on map"
                      >
                        <Eye class="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        @click="emit('delete-feature', feature)"
                        class="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        title="Delete feature"
                      >
                        <Trash2 class="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        @click="toggleRowExpansion(feature, index)"
                        class="h-8 w-8 p-0"
                        :title="isRowExpanded(feature, index) ? 'Close editor' : 'Edit feature'"
                      >
                        <Edit2 class="w-4 h-4" :class="isRowExpanded(feature, index) ? 'text-blue-600' : ''" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

                <!-- Expanded Details Row with Form -->
                <TableRow
                  v-if="isRowExpanded(feature, index)"
                  class="bg-muted/50 border-t"
                >
                  <TableCell colspan="3" class="p-4">
                    <div class="max-w-2xl">
                      <FormFeature
                        :key="`${getFeatureKey(feature, index)}-${props.selectedProject?.project_id || 'no-project'}`"
                        :feature="prepareFeatureForForm(feature)"
                        :predefined-project-id="props.selectedProject?.project_id?.toString()"
                        :hide-close="true"
                        @update:created="handleFeatureCreated(feature, $event)"
                        @update:saved="handleFeatureSaved(feature, $event)"
                        @update:feature="handleFeatureUpdate(feature, $event)"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              </template>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="fileGroups.length === 0" class="text-center py-12">
      <FileText class="w-12 h-12 text-muted-foreground mx-auto mb-4" />
      <h3 class="text-lg font-medium mb-2">No features found</h3>
      <p class="text-muted-foreground">
        Import some files to get started.
      </p>
    </div>
  </div>
</template>
